import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import cx from 'classnames';

import Chip from '@primitives/Chip';
import AddIcon from '@icons/AddIcon';
import ArrowDownSolidIcon from '@icons/ArrowDownSolidIcon';
import ArrowUpSolidIcon from '@icons/ArrowUpSolidIcon';
import CloseIcon from '@icons/CloseIcon';
import RequiredIcon from '@icons/RequiredIcon';
import Typography from '@primitives/Typography';
import { inputStates } from '@primitives/BorderedInput/constants';

import { AllOption, MobileMultiSelectConstants } from '@components/MobileMultiSelect/constants';
import { SelectedOptionType } from '@components/MobileMultiSelect/types';
import { transformSelectedOptions, sortedOptions } from '@components/MobileMultiSelect/utils';
import MobileMultiSelectStyles from '@components/MobileMultiSelect/MobileMultiSelect.module.scss';
import SelectPicker from '@components/SelectPicker';
import { SelectPickerValue, SelectPickerOptionProps } from '@components/SelectPicker/types';

import { useOutsideClick, useDeepCompareEffect } from '@hooks';

import { AutoCompleteProps, AutoCompleteInputVariantType } from './types';
import { getInputVariantClass, resetPickerOptions } from './utils';
import styles from './AutoComplete.module.scss';
import LoadingIcon from '@icons/LoadingIcon';

const AutoComplete = forwardRef<HTMLInputElement, AutoCompleteProps>(
    (
        {
            variant = 'borderless',
            options,
            noResultTitle = 'No results found!',
            noResultSubTitle = '',
            noResultContent = null,
            inputLabel = '',
            selectedOptions = [],
            placeholder = 'type here to search',
            limitChipThreshold = 0,
            updateSelectedOptions,
            updateClearAllCallback,
            updateOnChipRemoval,
            persistSearchAfterSelection = false,
            enableSelectedOptionSorting = false,
            showSelectAll = false,
            selectAllSubtext = '',
            limitChipThresholdMsg = '',
            dropdownIconContainerClass = '',
            onBlur,
            onFocus,
            error,
            errorIcon = null,
            errorHelperTextClasses = '',
            required,
            disabled = false,
            autoCompletePickerWrapper = '',
            labelClasses = '',
            persistMenuOnSelect = false,
            openAsMenu = false,
            allowNewOption = false,
            containerClassName = '',
            isServerSide = false,
            showLoader = false,
            onSearchChange,
            customOptionTemplate,
            maxSelectedValueThreshold = {
                count: 0,
                message: '',
            },
            showSelectedCount = false,
            showRemoveChip = true,
            showClearAll = true,
            ...restInputProps
        },
        ref,
    ) => {
        const finalInputStatus = error ? inputStates.error : inputStates.default;
        const [showPicker, setShowPicker] = useState<boolean>(false);
        const [inputValue, setInputValue] = useState<string>('');
        const [pickerOptions, setPickerOptions] = useState<Array<SelectPickerOptionProps>>(options);
        const [selectedValues, setSelectedValues] = useState<Array<SelectPickerValue>>([]);
        const [optionsToDisplay, setOptionsToDisplay] = useState<Array<SelectedOptionType>>([]);
        const [clearAllState, setClearAllState] = useState<boolean>(false);
        const [inputStatus, setInputStatus] =
            useState<AutoCompleteInputVariantType>(finalInputStatus);
        const [emptyState, setEmptyState] = useState<boolean>(false);

        const { moreCTA, selectedMessage, allSelected } = MobileMultiSelectConstants;
        const autoCompleteRef = useRef(null);
        const inputRef = useRef<HTMLInputElement>(null);

        const [showError, setShowError] = useState(error);

        useEffect(() => {
            setShowError(error);
            setInputStatus(error ? inputStates.error : inputStates.default);
        }, [error]);

        // Expose the inputRef to the parent component
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        useEffect(() => {
            if (options?.length) {
                setPickerOptions(options);
                setEmptyState(false);
            } else {
                setEmptyState(true);
            }
        }, [options]);

        useEffect(() => {
            if (inputStatus === inputStates.active) {
                inputRef?.current?.focus();
            }
        }, [inputStatus]);

        const setSelectedOptionsAsChips = () => {
            const selectedOptionsMap = new Map(selectedOptions.map((option) => [option, option]));
            const selectedOptionsToDisplay: Array<SelectPickerOptionProps> = [];
            options?.forEach((option) => {
                if (selectedOptionsMap.has(option?.value)) {
                    selectedOptionsToDisplay.push(option);
                }
            });
            return selectedOptionsToDisplay;
        };

        useEffect(() => {
            if (isServerSide) {
                return;
            }
            if (inputValue?.length) {
                const filteredPickerOptions = pickerOptions.filter(
                    (option) =>
                        option.label
                            ?.toLocaleLowerCase()
                            ?.indexOf(inputValue?.toLocaleLowerCase()) > -1,
                );
                if (!filteredPickerOptions.length) {
                    togglePicker(false);
                    if (!allowNewOption) setEmptyState(true);
                } else setEmptyState(false);
            }
        }, [inputValue]);

        /** handle the autocomplete default selectedOptions state */
        useDeepCompareEffect(() => {
            if (isServerSide) {
                setSelectedValues(selectedOptions || []);
                const selectedOptionsToDisplay = setSelectedOptionsAsChips();
                checkMaxSelectedValueThreshold(selectedOptionsToDisplay);
                setOptionsToDisplay(transformSelectedOptions(selectedOptionsToDisplay));
                return;
            }
            if (!selectedOptions?.length) {
                setSelectedValues(selectedOptions);
                const selectedOptionsToDisplay: Array<SelectPickerOptionProps> = [];
                setOptionsToDisplay(selectedOptionsToDisplay);

                if (updateSelectedOptions) {
                    updateSelectedOptions([]);
                }

                return;
            }

            setSelectedValues(selectedOptions);

            /** Set the options as chip */
            const selectedOptionsToDisplay = setSelectedOptionsAsChips();

            if (!selectedOptionsToDisplay?.length) {
                if (updateSelectedOptions) {
                    updateSelectedOptions([]);
                }
            }

            setOptionsToDisplay(transformSelectedOptions(selectedOptionsToDisplay));

            if (enableSelectedOptionSorting) {
                const transformedOptions = transformSelectedOptions(selectedOptionsToDisplay);
                const sortedPickerOptions = sortedOptions(options, transformedOptions);

                if (Array.isArray(sortedPickerOptions)) {
                    setPickerOptions(sortedPickerOptions);
                    setSelectedValues(transformedOptions?.map((option) => option?.value));
                }
            }
        }, [selectedOptions, options]);

        const togglePicker = (bool: boolean) => {
            setShowPicker(bool);
        };

        const handleSorting = (selectedOptionsFromPicker: SelectedOptionType[]): void => {
            const sortedPickerOptions = sortedOptions(pickerOptions, selectedOptionsFromPicker);
            if (Array.isArray(sortedPickerOptions) && enableSelectedOptionSorting) {
                setPickerOptions(sortedPickerOptions);
                setSelectedValues(selectedOptionsFromPicker?.map((option) => option?.value));
            }
        };

        const checkMaxSelectedValueThreshold = (selectedOptions: SelectedOptionType[]) => {
            if (showError) {
                setShowError('');
                setInputStatus(inputStates.default);
            }
            if (
                maxSelectedValueThreshold?.count &&
                selectedOptions.length > maxSelectedValueThreshold?.count
            ) {
                setShowPicker(false);
                setShowError(maxSelectedValueThreshold.message || '');
                setInputStatus(inputStates.error);
                return false;
            }
            return true;
        };

        const onSelectionChange = (option: SelectPickerOptionProps[] | SelectPickerOptionProps) => {
            const transformedOptions = transformSelectedOptions(option);
            const isValid = checkMaxSelectedValueThreshold(transformedOptions);
            if (!isValid) {
                return;
            }
            if (inputValue.length) {
                const finalOptions = [...optionsToDisplay, ...transformedOptions];
                setOptionsToDisplay(finalOptions);
                setSelectedValues(finalOptions.map((option) => option.value));
                if (!persistSearchAfterSelection) {
                    setInputValue('');
                    setPickerOptions(options);
                    setEmptyState(false);
                }
                if (updateSelectedOptions) {
                    updateSelectedOptions(finalOptions);
                }
            } else {
                setOptionsToDisplay(transformedOptions);
                setSelectedValues(transformedOptions.map((option) => option.value));
                if (updateSelectedOptions) {
                    updateSelectedOptions(transformedOptions);
                }
            }

            if (!persistMenuOnSelect) togglePicker(false);
            handleSorting(transformedOptions);
        };

        const removeOptionChip = (
            e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
            option: SelectedOptionType,
        ): void => {
            e?.stopPropagation();
            const filteredOptions: SelectedOptionType[] = optionsToDisplay?.filter(
                (item) => item?.id !== option?.id,
            );
            setOptionsToDisplay(filteredOptions);
            setShowPicker(false);
            setSelectedValues(filteredOptions.map((option) => option.value));

            if (updateOnChipRemoval) {
                updateOnChipRemoval(filteredOptions);
            }
        };

        const resetOptionsToDisplay = (
            e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
        ): void => {
            e?.stopPropagation();
            setOptionsToDisplay([]);
            setSelectedValues([]);
            setClearAllState(true);
            setInputValue('');
            setPickerOptions(options);

            if (updateClearAllCallback) {
                updateClearAllCallback([]);
            }
        };

        const handleSelectAll = (
            option: SelectPickerOptionProps[] | SelectPickerOptionProps,
        ): void => {
            onSelectionChange(option);
        };

        const handleWrapperClick = () => {
            if (!showPicker) {
                setInputStatus(inputStates.active);
            } else {
                setInputStatus(inputStates.default);
            }
            togglePicker(!showPicker);
            setClearAllState(false);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value = '' } = e.target || {};
            if (isServerSide && onSearchChange && typeof onSearchChange === 'function') {
                setInputValue(value);
                onSearchChange(value);
                !showPicker && togglePicker(true);
                return;
            }
            if (value === '') {
                setPickerOptions(resetPickerOptions(options));
                setEmptyState(false);
                !showPicker && togglePicker(true);
            } else {
                !showPicker && togglePicker(true);
            }
            setInputValue(value);
        };

        const handleInputEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (allowNewOption && e?.key === 'Enter' && e?.target?.value?.length) {
                const inputValue = e?.target?.value;
                const newOption = {
                    label: `${inputValue}`,
                    value: inputValue,
                };
                optionsToDisplay.push(newOption);
                pickerOptions.push(newOption);
                setSelectedValues(optionsToDisplay.map((option) => option.value));
                setInputValue('');
                const transformedOption = transformSelectedOptions([newOption]);
                handleSorting(transformedOption);
            }
        };

        const returnSelectedOptionsAsChips = (): JSX.Element | null => {
            if (optionsToDisplay?.length) {
                const remainingChipCount = optionsToDisplay?.length - limitChipThreshold;
                const finalOptions =
                    limitChipThreshold > 0
                        ? optionsToDisplay?.slice(0, limitChipThreshold)
                        : optionsToDisplay;
                if (
                    showSelectAll &&
                    !inputValue.length &&
                    optionsToDisplay.length === pickerOptions.length
                ) {
                    if (!showSelectedCount) {
                        return (
                            <Chip
                                key={AllOption.value}
                                label={AllOption.label}
                                wrapperClasses={
                                    MobileMultiSelectStyles['mobile-multi-select__chip']
                                }
                                onChipClick={(e) => {
                                    e?.stopPropagation();
                                }}
                                RightAdornment={
                                    <CloseIcon onClick={(e) => resetOptionsToDisplay(e)} />
                                }
                                disabled={disabled}
                            />
                        );
                    } else {
                        return (
                            <span
                                data-testid="autocomplete-limit-content"
                                className={
                                    MobileMultiSelectStyles[
                                        'mobile-multi-select__limit-count-content'
                                    ]
                                }
                            >
                                <Typography as="span" variant="p2">
                                    {allSelected}
                                </Typography>
                            </span>
                        );
                    }
                }
                return (
                    <>
                        {!showSelectedCount &&
                            finalOptions?.map((option) => (
                                <Chip
                                    key={option?.id}
                                    label={option?.label}
                                    wrapperClasses={
                                        MobileMultiSelectStyles['mobile-multi-select__chip']
                                    }
                                    labelClasses={styles['autocomplete-chip']}
                                    onChipClick={(e) => {
                                        e?.stopPropagation();
                                    }}
                                    RightAdornment={
                                        showRemoveChip ? (
                                            <CloseIcon
                                                onClick={(e) => removeOptionChip(e, option)}
                                            />
                                        ) : null
                                    }
                                    disabled={disabled}
                                />
                            ))}
                        {!showSelectedCount && limitChipThreshold && remainingChipCount > 0 ? (
                            <span
                                data-testid="autocomplete-limit-content"
                                className={
                                    MobileMultiSelectStyles[
                                        'mobile-multi-select__limit-count-content'
                                    ]
                                }
                            >
                                <AddIcon />
                                <Typography as="span" variant="p2">
                                    {remainingChipCount}{' '}
                                    {limitChipThresholdMsg?.length
                                        ? limitChipThresholdMsg
                                        : moreCTA}
                                </Typography>
                            </span>
                        ) : null}
                        {showSelectedCount && (
                            <span
                                data-testid="autocomplete-limit-content"
                                className={
                                    MobileMultiSelectStyles[
                                        'mobile-multi-select__limit-count-content'
                                    ]
                                }
                            >
                                <Typography as="span" variant="p2">
                                    {optionsToDisplay?.length} {selectedMessage}
                                </Typography>
                            </span>
                        )}
                    </>
                );
            }
            return null;
        };

        const returnClearAllIcon = (): JSX.Element | false => {
            return (
                showClearAll &&
                optionsToDisplay?.length > 0 && (
                    <CloseIcon data-testid="close-icon" onClick={(e) => resetOptionsToDisplay(e)} />
                )
            );
        };

        const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
            if (inputStatus === inputStates.active) {
                setInputStatus(inputStates.default);
            }

            /* To pass the current event to the consumer */
            if (onBlur) {
                onBlur(event);
            }
        };

        const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
            if (inputStatus === inputStates.default) {
                setInputStatus(inputStates.active);
            }
            /* To pass the current event to the consumer */
            if (onFocus) {
                onFocus(event);
            }
        };

        const getErrorHelperTextMessage = (): JSX.Element | false => {
            return (
                typeof showError === 'string' &&
                showError !== '' && (
                    <div
                        data-testid="autocomplete-error-help-text-wrapper"
                        className={cx(
                            styles['borderless-autocomplete__helper-text-wrapper'],
                            errorHelperTextClasses,
                        )}
                    >
                        <div
                            className={
                                styles[
                                    'borderless-autocomplete__helper-text-wrapper-text-msgs--icon'
                                ]
                            }
                        >
                            {errorIcon !== null && errorIcon}
                        </div>
                        <Typography
                            as="span"
                            variant="p2"
                            className={
                                styles[
                                    'borderless-autocomplete__helper-text-wrapper-text-msgs--error'
                                ]
                            }
                        >
                            {showError}
                        </Typography>
                    </div>
                )
            );
        };

        const returnInputLabel = () => {
            const returnLabelComponent = (): JSX.Element => {
                return (
                    <div
                        role="autocomplete-label"
                        className={cx(
                            styles['autocomplete-input-label'],
                            variant === 'borderless'
                                ? styles['autocomplete-input-label--append-animate']
                                : '',
                            labelClasses,
                        )}
                    >
                        <Typography
                            variant="p3"
                            className={styles['autocomplete-input-label--content']}
                        >
                            {inputLabel}
                        </Typography>
                        {required && (
                            <RequiredIcon
                                className={styles['autocomplete-input-label--required']}
                            />
                        )}
                    </div>
                );
            };

            const returnBorderlessVariantLabel = () => {
                if (
                    variant === 'borderless' &&
                    (inputStatus === inputStates.active || optionsToDisplay?.length)
                ) {
                    return returnLabelComponent();
                } else null;
            };

            if (inputLabel !== '') {
                return variant === 'borderless'
                    ? returnBorderlessVariantLabel()
                    : returnLabelComponent();
            }
            return null;
        };

        const emptyStateContent = (): JSX.Element | null => {
            if (showLoader) {
                return (
                    <div
                        data-testid="autocomplete-loader-content"
                        className={styles['autocomplete-empty-state-wrapper']}
                    >
                        <LoadingIcon />
                    </div>
                );
            }
            if (emptyState) {
                return noResultContent !== null ? (
                    <div
                        data-testid="autocomplete-no-result-content"
                        className={styles['autocomplete-empty-state-wrapper']}
                    >
                        {noResultContent}
                    </div>
                ) : (
                    <div className={styles['autocomplete-empty-state-wrapper']}>
                        <Typography
                            data-testid="autocomplete-no-result-title"
                            variant="p2"
                            className={styles['autocomplete-empty-state-wrapper--title']}
                        >
                            {noResultTitle}
                        </Typography>
                        {noResultSubTitle?.length > 0 && (
                            <Typography
                                data-testid="autocomplete-no-result-sub-title"
                                variant="p5"
                                className={styles['autocomplete-empty-state-wrapper--sub-title']}
                            >
                                {noResultSubTitle}
                            </Typography>
                        )}
                    </div>
                );
            }
            return null;
        };

        useOutsideClick(autoCompleteRef, () => {
            if (showPicker) {
                togglePicker(false);
            }
        });

        const ArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;
        const selectPickerOptions = isServerSide ? options : pickerOptions;

        return (
            <div
                ref={autoCompleteRef}
                className={cx(styles.container, {
                    [styles.autocompleteAndSelectWrapper]: openAsMenu,
                })}
            >
                {returnInputLabel()}

                <div
                    role="autocomplete"
                    className={cx(
                        styles['autocomplete-wrapper'],
                        variant === 'bordered'
                            ? styles['bordered-wrapper']
                            : styles['borderless-autocomplete'],
                        getInputVariantClass(variant, inputStatus),
                        {
                            [styles['autocomplete-wrapper--disabled']]: disabled,
                        },
                        containerClassName,
                    )}
                    onClick={handleWrapperClick}
                >
                    <div className={styles['autocomplete-container']}>
                        <div
                            data-testid="autocomplete-chip-container"
                            className={styles['chip-container']}
                        >
                            {returnSelectedOptionsAsChips()}
                            {(optionsToDisplay?.length === 0 || !disabled) && (
                                <input
                                    role="autocomplete-input"
                                    disabled={disabled}
                                    value={inputValue}
                                    placeholder={placeholder}
                                    onFocus={handleOnFocus}
                                    onBlur={handleOnBlur}
                                    onChange={handleInputChange}
                                    onKeyDown={handleInputEnter}
                                    ref={inputRef}
                                    {...restInputProps}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles['autocomplete-icons-container']}>
                        {returnClearAllIcon()}
                        <div className={dropdownIconContainerClass}>
                            <ArrowIcon width={9} />
                        </div>
                    </div>
                </div>
                {!showLoader && showPicker && selectPickerOptions?.length ? (
                    <SelectPicker
                        selectedValues={selectedValues}
                        onSelectionChange={onSelectionChange}
                        options={selectPickerOptions}
                        clearAll={clearAllState}
                        wrapperClasses={cx(
                            styles['autocomplete-picker-wrapper'],
                            { [styles['autocomplete-picker-wrapper--menu']]: openAsMenu },
                            autoCompletePickerWrapper,
                        )}
                        selectPickerItemClassName={
                            MobileMultiSelectStyles['mobile-multi-select__select-picker-option']
                        }
                        selectAllClass={MobileMultiSelectStyles['mobile-multi-select__select-all']}
                        pickerCheckboxClass={styles['picker-checkbox-wrapper']}
                        showSelectAll={showSelectAll && !inputValue.length}
                        updateSelectedOptions={handleSelectAll}
                        selectAllSubtext={selectAllSubtext}
                        multiSelect
                        hideBottomCTAs
                        updateSearch={isServerSide ? '' : inputValue}
                        customOptionTemplate={customOptionTemplate}
                    />
                ) : null}
                {showPicker ? emptyStateContent() : null}
                {getErrorHelperTextMessage()}
            </div>
        );
    },
);

export default AutoComplete;
